import { ApolloLink, createHttpLink, split } from '@apollo/client';
import { Endpoint } from '../../models/api.model';
import { authAppEndpointMiddlewareLink } from './authLinks';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';

import {
  buildPostFromGetUrl,
  jsonResponse,
  generateCacheKey,
  parseGetUrlForCacheInputs,
  readLocalStorageCache,
  readMemoryCache,
  writeLocalStorageCache,
  writeMemoryCache,
} from './directusLocalStorageCache';

const DEFAULT_CACHE_MINUTES = 15;
const DIRECTUS_GET_EXCLUDE_OPERATION_NAMES = new Set<string>([
  'GetBingoPageData',
  'GetSpinsPageData',
  'GetItvCollectionBySlug',
]);

function parseMinutesMap(raw: string | undefined | null): Record<string, number> {
  if (!raw) return {};

  try {
    const obj = JSON.parse(raw) as Record<string, unknown>;
    const out: Record<string, number> = {};

    for (const [k, v] of Object.entries(obj)) {
      const n = typeof v === 'number' ? v : parseInt(String(v), 10);

      if (!Number.isNaN(n) && n > 0) out[k] = n;
    }

    return out;
  } catch {
    return {};
  }
}

function minutesToMs(minutes: number): number {
  return minutes * 60 * 1000;
}

const DIRECTUS_CACHE_BUSTER = String(process.env.REACT_APP_DIRECTUS_CACHE_BUSTER ?? '').trim();

function withLocalStorageCacheBuster(key: string): string {
  return DIRECTUS_CACHE_BUSTER ? `${key}::${DIRECTUS_CACHE_BUSTER}` : key;
}

const DIRECTUS_LS_TTLS_MINUTES = parseMinutesMap(process.env.REACT_APP_DIRECTUS_LS_CACHE_TTLS);
const DIRECTUS_MEMORY_CACHE_MINUTES = parseInt(process.env.REACT_APP_DIRECTUS_MEMORY_CACHE_MINUTES ?? '', 10);
const DIRECTUS_MEMORY_TTL_MS =
  (isNaN(DIRECTUS_MEMORY_CACHE_MINUTES) ? DEFAULT_CACHE_MINUTES : DIRECTUS_MEMORY_CACHE_MINUTES) * 60 * 1000;

function resolveLocalStorageTtlMs(opName: string): number | null {
  const mins = DIRECTUS_LS_TTLS_MINUTES[opName];

  if (!Number.isFinite(mins) || mins <= 0) return null;

  return minutesToMs(mins);
}

const DIRECTUS_CACHE_DISABLED = String(process.env.REACT_APP_DIRECTUS_CACHE_DISABLED ?? '').toLowerCase() === 'true';

type FetchInput = Parameters<typeof fetch>[0];

type ResolvedRequestInfo = {
  urlStr: string;
  headers: Headers;
  method: string;
};

function resolveRequestInfo(input: FetchInput, init?: RequestInit): ResolvedRequestInfo {
  if (typeof input === 'string') {
    return { urlStr: input, headers: new Headers(init?.headers), method: (init?.method || 'GET').toUpperCase() };
  }

  if (input instanceof URL) {
    return { urlStr: input.href, headers: new Headers(init?.headers), method: (init?.method || 'GET').toUpperCase() };
  }

  return {
    urlStr: input.url,
    headers: new Headers(input.headers),
    method: (input.method || init?.method || 'GET').toUpperCase(),
  };
}

const directusSmartFetch: typeof fetch = async (input, init) => {
  const { urlStr, headers, method } = resolveRequestInfo(input, init);

  if (method !== 'GET') return fetch(input, init);

  const parsedGet = parseGetUrlForCacheInputs(urlStr);

  if (!parsedGet || !parsedGet.operationName) {
    return fetch(input, init);
  }

  const mustForcePost = DIRECTUS_GET_EXCLUDE_OPERATION_NAMES.has(parsedGet.operationName);

  const lsTtlMs = resolveLocalStorageTtlMs(parsedGet.operationName);
  const useLocalStorage = lsTtlMs !== null;

  const baseCacheKey = !DIRECTUS_CACHE_DISABLED
    ? generateCacheKey(parsedGet.operationName, parsedGet.query, parsedGet.variables, parsedGet.extensions)
    : null;

  const cacheKey = baseCacheKey ? (useLocalStorage ? withLocalStorageCacheBuster(baseCacheKey) : baseCacheKey) : null;

  if (!DIRECTUS_CACHE_DISABLED && cacheKey) {
    const cached = useLocalStorage ? readLocalStorageCache(cacheKey) : readMemoryCache(cacheKey);

    if (cached !== null) return jsonResponse(cached);
  }

  if (mustForcePost) {
    const { postUrl, postInit, operationName, bodyObj } = buildPostFromGetUrl(urlStr, headers, init);

    if (!DIRECTUS_CACHE_DISABLED) {
      const effectiveOp = operationName ?? parsedGet.operationName;
      const q = typeof bodyObj.query === 'string' ? bodyObj.query : parsedGet.query;

      const postBaseKey = generateCacheKey(effectiveOp, q, bodyObj.variables, bodyObj.extensions);

      const postLsTtlMs = resolveLocalStorageTtlMs(effectiveOp);
      const postUseLocalStorage = postLsTtlMs !== null;

      const postKey = postUseLocalStorage ? withLocalStorageCacheBuster(postBaseKey) : postBaseKey;

      const cachedPost = postUseLocalStorage ? readLocalStorageCache(postKey) : readMemoryCache(postKey);

      if (cachedPost !== null) return jsonResponse(cachedPost);

      const res = await fetch(postUrl, postInit);

      if (res.ok) {
        try {
          const json = await res.clone().json();

          if (postUseLocalStorage && postLsTtlMs !== null) {
            writeLocalStorageCache(postKey, json, postLsTtlMs);
          } else {
            writeMemoryCache(postKey, json, DIRECTUS_MEMORY_TTL_MS);
          }
        } catch {
          /* ignore */
        }
      }

      return res;
    }

    return fetch(postUrl, postInit);
  }

  const res = await fetch(input, init);

  if (res.status === 414) {
    const { postUrl, postInit, operationName, bodyObj } = buildPostFromGetUrl(urlStr, headers, init);

    if (!DIRECTUS_CACHE_DISABLED) {
      const effectiveOp = operationName ?? parsedGet.operationName;
      const q = typeof bodyObj.query === 'string' ? bodyObj.query : parsedGet.query;

      const postBaseKey = generateCacheKey(effectiveOp, q, bodyObj.variables, bodyObj.extensions);

      const postLsTtlMs = resolveLocalStorageTtlMs(effectiveOp);
      const postUseLocalStorage = postLsTtlMs !== null;

      const postKey = postUseLocalStorage ? withLocalStorageCacheBuster(postBaseKey) : postBaseKey;

      const cachedPost = postUseLocalStorage ? readLocalStorageCache(postKey) : readMemoryCache(postKey);

      if (cachedPost !== null) return jsonResponse(cachedPost);

      const postRes = await fetch(postUrl, postInit);

      if (postRes.ok) {
        try {
          const json = await postRes.clone().json();

          if (postUseLocalStorage && postLsTtlMs !== null) {
            writeLocalStorageCache(postKey, json, postLsTtlMs);
          } else {
            writeMemoryCache(postKey, json, DIRECTUS_MEMORY_TTL_MS);
          }
        } catch {
          /* ignore */
        }
      }

      return postRes;
    }

    return fetch(postUrl, postInit);
  }

  if (!DIRECTUS_CACHE_DISABLED && res.ok && cacheKey) {
    try {
      const json = await res.clone().json();

      if (useLocalStorage && lsTtlMs !== null) {
        writeLocalStorageCache(cacheKey, json, lsTtlMs);
      } else {
        writeMemoryCache(cacheKey, json, DIRECTUS_MEMORY_TTL_MS);
      }
    } catch {
      /* ignore */
    }
  }

  return res;
};

const appApiURL = createHttpLink({
  uri: process.env.REACT_APP_BASE_API_URL,
});

const appAPIURLWithAuth = ApolloLink.from([authAppEndpointMiddlewareLink, appApiURL]);

const appApiURLWss = new GraphQLWsLink(
  createClient({
    url: String(process.env.REACT_APP_BASE_API_URL_WSS),
    shouldRetry: () => true,
    retryAttempts: 5,
    keepAlive: 30000,
  })
);

const appAPIURLWithAuthWss = ApolloLink.from([authAppEndpointMiddlewareLink, appApiURLWss]);

const betzoneDirectusAPI = createHttpLink({
  uri: process.env.REACT_APP_BETZONE_DIRECTUS_API_URL,
  useGETForQueries: true,
  fetch: directusSmartFetch,
});

const betzoneDirectusApiUrlWss = new GraphQLWsLink(
  createClient({
    url: String(process.env.REACT_APP_BETZONE_DIRECTUS_API_URL_WSS),
    shouldRetry: () => true,
    retryAttempts: 2,
    keepAlive: 30000,
    connectionParams: async () => {
      return { access_token: `${process.env.REACT_APP_BETZONE_API_TOKEN}` };
    },
  })
);

const initApiLinks = split(
  (operation) => {
    return operation.getContext().endpoint === Endpoint.appAPI || !operation.getContext().endpoint;
  },
  appAPIURLWithAuth,
  betzoneDirectusAPI
);

const interimApiLinks = split(
  (operation) => {
    const definition = getMainDefinition(operation.query);

    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  appAPIURLWithAuthWss,
  initApiLinks
);

export const apiLinks = split(
  (operation) => {
    const definition = getMainDefinition(operation.query);

    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription' &&
      (definition.name?.value === 'OnMessageCreated' || definition.name?.value === 'OnJackpotUpdated')
    );
  },
  betzoneDirectusApiUrlWss,
  interimApiLinks
);
